<template>
  <div class="closed-jobs">
    <div v-if="loading">
      <div v-for="value in 3" :key="value" class="animate-pulse bg-grey-divider-subdued h-112 w-full mb-16" />
    </div>
    <div v-else-if="hasClosedJobs">
      <SeekerJobCard
        v-for="job in jobs"
        :key="job.id"
        :job="job"
        :account="account"
        class="mb-16"
        @click="handleJobClick"
        @manage-job="onManageJob"
      />
      <Button
        v-if="hasNextClosedPage"
        variant="secondary"
        class="cursor-pointer w-full mb-64 sm:flex sm:w-auto sm:mx-auto"
        :loading="loadingMore"
        @click.prevent="loadMore"
        >View More</Button
      >
    </div>
    <div v-else class="closed-jobs__empty">
      <h2 class="text-h4 w-full mb-8">No closed jobs</h2>
      <p class="text-body mb-24">Close your job when you've hired a sitter or no longer have a care need</p>
    </div>
    <ActionsPopup v-model="showManagePopup" title="Manage Job" :buttons="manageJobButtons">
      <p v-if="currentJob?.title" class="text-body">{{ currentJob?.title }}</p>
    </ActionsPopup>
    <ActionsPopup v-model="showCloseConfirmation" title="Close this job?" :buttons="closeConfirmationButtons">
      <p class="text-body">Once you close this job, new sitters will no longer be able to apply for it</p>
    </ActionsPopup>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ActionsPopup from '@/components/actionsPopup/ActionsPopup.vue';
import type { ActionButton } from '@/components/actionsPopup/types';
import SeekerJobCard from '@/components/seekerJobCard/SeekerJobCard.vue';
import Button from '@/components/button/Button.vue';
import { redirect } from '@/helpers/redirects';
import { createViewApplicantsButton, createReopenJobButton, createCloseConfirmationButtons } from '@/helpers/jobActionButtons';
import { FETCH_CLOSED_JOBS } from '@/graphql/account/queries';

export default defineComponent({
  name: 'SeekerJobsClosed',
  components: {
    ActionsPopup,
    SeekerJobCard,
    Button,
  },
  apollo: {
    closedJobs: {
      query: FETCH_CLOSED_JOBS,
      update(data) {
        return data.viewer?.jobs || null;
      },
      // Called when the query completes
      result() {
        this.loading = false;
      },
      // Called if the query errors
      error() {
        this.loading = false;
      },
    },
  },
  data(): {
    closedJobs: any;
    loading: boolean;
    loadingMore: boolean;
    currentJob: any;
    showManagePopup: boolean;
    showCloseConfirmation: boolean;
  } {
    return {
      closedJobs: null,
      loading: true,
      loadingMore: false,
      currentJob: null,
      showManagePopup: false,
      showCloseConfirmation: false,
    };
  },
  computed: {
    closeConfirmationButtons(): ActionButton[] {
      return createCloseConfirmationButtons({
        onConfirm: () => this.closeJobMutation(),
        onCancel: () => this.cancelCloseConfirmation(),
      });
    },
    manageJobButtons(): ActionButton[] {
      const job = this.currentJob;
      if (!job) return [];
      const hasApplicants = job.applications.totalCount > 0;
      const currentUserOwnsJob = this.account.id === job.parent.id;

      const buttons: ActionButton[] = [];

      // the list of jobs returned for an account includes jobs that have been shared with the account
      // this checks that the user is the manager of the job
      if (currentUserOwnsJob) {
        buttons.push(createReopenJobButton(job.id));
      }

      if (hasApplicants) {
        buttons.push(createViewApplicantsButton(job.id));
      }
      return buttons;
    },
    hasClosedJobs(): boolean {
      return !!(this.closedJobs && this.closedJobs.totalCount > 0);
    },
    jobs(): Array<any> {
      if (this.closedJobs && this.closedJobs.edges) {
        return this.closedJobs.edges.map((edge: any) => edge.node);
      }
      return [];
    },
    account() {
      return this.$store.getters['account/getAccount'];
    },
    hasNextClosedPage(): boolean {
      return this.closedJobs?.pageInfo?.hasNextPage || false;
    },
  },
  methods: {
    onManageJob(job: any) {
      this.currentJob = job;
      this.showManagePopup = true;
    },
    cancelCloseConfirmation() {
      this.showCloseConfirmation = false;
      this.showManagePopup = true;
    },
    async closeJobMutation() {
      this.showCloseConfirmation = false;
      if (!this.currentJob) return;
      const jobId = this.currentJob.id;
      try {
        await this.$store.dispatch('job/closeJob', jobId);
        redirect(`/jobs/action_with_uuid?job_action=job_closed_questionnaire&uuid=${jobId}`);
      } catch {
        // Error already logged in store action
      }
    },
    handleJobClick(card: any, job: any) {
      if (!job) return;

      this.currentJob = job;
      this.showManagePopup = true;
    },
    getLastCursor(): string | null {
      return this.closedJobs?.pageInfo?.endCursor || null;
    },
    async loadMore() {
      const after = this.getLastCursor();
      if (!after || this.loadingMore) return;

      this.loadingMore = true;
      try {
        await this.$apollo.queries.closedJobs.fetchMore({
          variables: {
            after,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            if (!fetchMoreResult || !fetchMoreResult.viewer?.jobs) {
              return previousResult;
            }

            return {
              viewer: {
                ...previousResult.viewer,
                jobs: {
                  ...fetchMoreResult.viewer.jobs,
                  edges: [...previousResult.viewer.jobs.edges, ...fetchMoreResult.viewer.jobs.edges],
                },
              },
            };
          },
        });
      } catch (error) {
        console.error('Error loading more closed jobs:', error);
      } finally {
        this.loadingMore = false;
      }
    },
  },
});
</script>
